/*
 * THIS FILE WAS GENERATED BY "@atproto/lex". DO NOT EDIT.
 */

import { l } from '@atproto/lex'

const $nsid = 'app.bsky.ageassurance.defs'

export { $nsid }

/** The access level granted based on Age Assurance data we've processed. */
type Access = 'unknown' | 'none' | 'safe' | 'full' | l.UnknownString

export type { Access }

/** The access level granted based on Age Assurance data we've processed. */
const access = /*#__PURE__*/ l.string<{
  knownValues: ['unknown', 'none', 'safe', 'full']
}>()

export { access }

/** The status of the Age Assurance process. */
type Status = 'unknown' | 'pending' | 'assured' | 'blocked' | l.UnknownString

export type { Status }

/** The status of the Age Assurance process. */
const status = /*#__PURE__*/ l.string<{
  knownValues: ['unknown', 'pending', 'assured', 'blocked']
}>()

export { status }

/** The user's computed Age Assurance state. */
type State = {
  $type?: 'app.bsky.ageassurance.defs#state'

  /**
   * The timestamp when this state was last updated.
   */
  lastInitiatedAt?: l.DatetimeString
  status: Status
  access: Access
}

export type { State }

/** The user's computed Age Assurance state. */
const state = /*#__PURE__*/ l.typedObject<State>(
  $nsid,
  'state',
  /*#__PURE__*/ l.object({
    lastInitiatedAt: /*#__PURE__*/ l.optional(
      /*#__PURE__*/ l.string({ format: 'datetime' }),
    ),
    status: /*#__PURE__*/ l.ref<Status>((() => status) as any),
    access: /*#__PURE__*/ l.ref<Access>((() => access) as any),
  }),
)

export { state }

/** Additional metadata needed to compute Age Assurance state client-side. */
type StateMetadata = {
  $type?: 'app.bsky.ageassurance.defs#stateMetadata'

  /**
   * The account creation timestamp.
   */
  accountCreatedAt?: l.DatetimeString
}

export type { StateMetadata }

/** Additional metadata needed to compute Age Assurance state client-side. */
const stateMetadata = /*#__PURE__*/ l.typedObject<StateMetadata>(
  $nsid,
  'stateMetadata',
  /*#__PURE__*/ l.object({
    accountCreatedAt: /*#__PURE__*/ l.optional(
      /*#__PURE__*/ l.string({ format: 'datetime' }),
    ),
  }),
)

export { stateMetadata }

type Config = {
  $type?: 'app.bsky.ageassurance.defs#config'

  /**
   * The per-region Age Assurance configuration.
   */
  regions: ConfigRegion[]
}

export type { Config }

const config = /*#__PURE__*/ l.typedObject<Config>(
  $nsid,
  'config',
  /*#__PURE__*/ l.object({
    regions: /*#__PURE__*/ l.array(
      /*#__PURE__*/ l.ref<ConfigRegion>((() => configRegion) as any),
    ),
  }),
)

export { config }

/** The Age Assurance configuration for a specific region. */
type ConfigRegion = {
  $type?: 'app.bsky.ageassurance.defs#configRegion'

  /**
   * The ISO 3166-1 alpha-2 country code this configuration applies to.
   */
  countryCode: string

  /**
   * The ISO 3166-2 region code this configuration applies to. If omitted, the configuration applies to the entire country.
   */
  regionCode?: string

  /**
   * The minimum age (as a whole integer) required to use Bluesky in this region.
   */
  minAccessAge: number

  /**
   * Verification methods permitted in this region in addition to the third-party (KWS) flow, which is always supported. `device` permits using the native on-device age APIs (e.g. Apple Declared Age Range, Google Play Age Signals).
   */
  additionalVerificationMethods?: ('device' | l.UnknownString)[]

  /**
   * The ordered list of Age Assurance rules that apply to this region. Rules should be applied in order, and the first matching rule determines the access level granted. The rules array should always include a default rule as the last item.
   */
  rules: (
    | l.$Typed<ConfigRegionRuleDefault>
    | l.$Typed<ConfigRegionRuleIfDeclaredOverAge>
    | l.$Typed<ConfigRegionRuleIfDeclaredUnderAge>
    | l.$Typed<ConfigRegionRuleIfAssuredOverAge>
    | l.$Typed<ConfigRegionRuleIfAssuredUnderAge>
    | l.$Typed<ConfigRegionRuleIfAccountNewerThan>
    | l.$Typed<ConfigRegionRuleIfAccountOlderThan>
    | l.Unknown$TypedObject
  )[]
}

export type { ConfigRegion }

/** The Age Assurance configuration for a specific region. */
const configRegion = /*#__PURE__*/ l.typedObject<ConfigRegion>(
  $nsid,
  'configRegion',
  /*#__PURE__*/ l.object({
    countryCode: /*#__PURE__*/ l.string(),
    regionCode: /*#__PURE__*/ l.optional(/*#__PURE__*/ l.string()),
    minAccessAge: /*#__PURE__*/ l.integer(),
    additionalVerificationMethods: /*#__PURE__*/ l.optional(
      /*#__PURE__*/ l.array(
        /*#__PURE__*/ l.string<{ knownValues: ['device'] }>(),
      ),
    ),
    rules: /*#__PURE__*/ l.array(
      /*#__PURE__*/ l.typedUnion(
        [
          /*#__PURE__*/ l.typedRef<ConfigRegionRuleDefault>(
            (() => configRegionRuleDefault) as any,
          ),
          /*#__PURE__*/ l.typedRef<ConfigRegionRuleIfDeclaredOverAge>(
            (() => configRegionRuleIfDeclaredOverAge) as any,
          ),
          /*#__PURE__*/ l.typedRef<ConfigRegionRuleIfDeclaredUnderAge>(
            (() => configRegionRuleIfDeclaredUnderAge) as any,
          ),
          /*#__PURE__*/ l.typedRef<ConfigRegionRuleIfAssuredOverAge>(
            (() => configRegionRuleIfAssuredOverAge) as any,
          ),
          /*#__PURE__*/ l.typedRef<ConfigRegionRuleIfAssuredUnderAge>(
            (() => configRegionRuleIfAssuredUnderAge) as any,
          ),
          /*#__PURE__*/ l.typedRef<ConfigRegionRuleIfAccountNewerThan>(
            (() => configRegionRuleIfAccountNewerThan) as any,
          ),
          /*#__PURE__*/ l.typedRef<ConfigRegionRuleIfAccountOlderThan>(
            (() => configRegionRuleIfAccountOlderThan) as any,
          ),
        ],
        false,
      ),
    ),
  }),
)

export { configRegion }

/** Age Assurance rule that applies by default. */
type ConfigRegionRuleDefault = {
  $type?: 'app.bsky.ageassurance.defs#configRegionRuleDefault'
  access: Access
}

export type { ConfigRegionRuleDefault }

/** Age Assurance rule that applies by default. */
const configRegionRuleDefault =
  /*#__PURE__*/ l.typedObject<ConfigRegionRuleDefault>(
    $nsid,
    'configRegionRuleDefault',
    /*#__PURE__*/ l.object({
      access: /*#__PURE__*/ l.ref<Access>((() => access) as any),
    }),
  )

export { configRegionRuleDefault }

/** Age Assurance rule that applies if the user has declared themselves equal-to or over a certain age. */
type ConfigRegionRuleIfDeclaredOverAge = {
  $type?: 'app.bsky.ageassurance.defs#configRegionRuleIfDeclaredOverAge'

  /**
   * The age threshold as a whole integer.
   */
  age: number
  access: Access
}

export type { ConfigRegionRuleIfDeclaredOverAge }

/** Age Assurance rule that applies if the user has declared themselves equal-to or over a certain age. */
const configRegionRuleIfDeclaredOverAge =
  /*#__PURE__*/ l.typedObject<ConfigRegionRuleIfDeclaredOverAge>(
    $nsid,
    'configRegionRuleIfDeclaredOverAge',
    /*#__PURE__*/ l.object({
      age: /*#__PURE__*/ l.integer(),
      access: /*#__PURE__*/ l.ref<Access>((() => access) as any),
    }),
  )

export { configRegionRuleIfDeclaredOverAge }

/** Age Assurance rule that applies if the user has declared themselves under a certain age. */
type ConfigRegionRuleIfDeclaredUnderAge = {
  $type?: 'app.bsky.ageassurance.defs#configRegionRuleIfDeclaredUnderAge'

  /**
   * The age threshold as a whole integer.
   */
  age: number
  access: Access
}

export type { ConfigRegionRuleIfDeclaredUnderAge }

/** Age Assurance rule that applies if the user has declared themselves under a certain age. */
const configRegionRuleIfDeclaredUnderAge =
  /*#__PURE__*/ l.typedObject<ConfigRegionRuleIfDeclaredUnderAge>(
    $nsid,
    'configRegionRuleIfDeclaredUnderAge',
    /*#__PURE__*/ l.object({
      age: /*#__PURE__*/ l.integer(),
      access: /*#__PURE__*/ l.ref<Access>((() => access) as any),
    }),
  )

export { configRegionRuleIfDeclaredUnderAge }

/** Age Assurance rule that applies if the user has been assured to be equal-to or over a certain age. */
type ConfigRegionRuleIfAssuredOverAge = {
  $type?: 'app.bsky.ageassurance.defs#configRegionRuleIfAssuredOverAge'

  /**
   * The age threshold as a whole integer.
   */
  age: number
  access: Access
}

export type { ConfigRegionRuleIfAssuredOverAge }

/** Age Assurance rule that applies if the user has been assured to be equal-to or over a certain age. */
const configRegionRuleIfAssuredOverAge =
  /*#__PURE__*/ l.typedObject<ConfigRegionRuleIfAssuredOverAge>(
    $nsid,
    'configRegionRuleIfAssuredOverAge',
    /*#__PURE__*/ l.object({
      age: /*#__PURE__*/ l.integer(),
      access: /*#__PURE__*/ l.ref<Access>((() => access) as any),
    }),
  )

export { configRegionRuleIfAssuredOverAge }

/** Age Assurance rule that applies if the user has been assured to be under a certain age. */
type ConfigRegionRuleIfAssuredUnderAge = {
  $type?: 'app.bsky.ageassurance.defs#configRegionRuleIfAssuredUnderAge'

  /**
   * The age threshold as a whole integer.
   */
  age: number
  access: Access
}

export type { ConfigRegionRuleIfAssuredUnderAge }

/** Age Assurance rule that applies if the user has been assured to be under a certain age. */
const configRegionRuleIfAssuredUnderAge =
  /*#__PURE__*/ l.typedObject<ConfigRegionRuleIfAssuredUnderAge>(
    $nsid,
    'configRegionRuleIfAssuredUnderAge',
    /*#__PURE__*/ l.object({
      age: /*#__PURE__*/ l.integer(),
      access: /*#__PURE__*/ l.ref<Access>((() => access) as any),
    }),
  )

export { configRegionRuleIfAssuredUnderAge }

/** Age Assurance rule that applies if the account is equal-to or newer than a certain date. */
type ConfigRegionRuleIfAccountNewerThan = {
  $type?: 'app.bsky.ageassurance.defs#configRegionRuleIfAccountNewerThan'

  /**
   * The date threshold as a datetime string.
   */
  date: l.DatetimeString
  access: Access
}

export type { ConfigRegionRuleIfAccountNewerThan }

/** Age Assurance rule that applies if the account is equal-to or newer than a certain date. */
const configRegionRuleIfAccountNewerThan =
  /*#__PURE__*/ l.typedObject<ConfigRegionRuleIfAccountNewerThan>(
    $nsid,
    'configRegionRuleIfAccountNewerThan',
    /*#__PURE__*/ l.object({
      date: /*#__PURE__*/ l.string({ format: 'datetime' }),
      access: /*#__PURE__*/ l.ref<Access>((() => access) as any),
    }),
  )

export { configRegionRuleIfAccountNewerThan }

/** Age Assurance rule that applies if the account is older than a certain date. */
type ConfigRegionRuleIfAccountOlderThan = {
  $type?: 'app.bsky.ageassurance.defs#configRegionRuleIfAccountOlderThan'

  /**
   * The date threshold as a datetime string.
   */
  date: l.DatetimeString
  access: Access
}

export type { ConfigRegionRuleIfAccountOlderThan }

/** Age Assurance rule that applies if the account is older than a certain date. */
const configRegionRuleIfAccountOlderThan =
  /*#__PURE__*/ l.typedObject<ConfigRegionRuleIfAccountOlderThan>(
    $nsid,
    'configRegionRuleIfAccountOlderThan',
    /*#__PURE__*/ l.object({
      date: /*#__PURE__*/ l.string({ format: 'datetime' }),
      access: /*#__PURE__*/ l.ref<Access>((() => access) as any),
    }),
  )

export { configRegionRuleIfAccountOlderThan }

/** Object used to store Age Assurance data in stash. */
type Event = {
  $type?: 'app.bsky.ageassurance.defs#event'

  /**
   * The date and time of this write operation.
   */
  createdAt: l.DatetimeString

  /**
   * The unique identifier for this instance of the Age Assurance flow, in UUID format.
   */
  attemptId: string

  /**
   * The status of the Age Assurance process.
   */
  status: 'unknown' | 'pending' | 'assured' | 'blocked' | l.UnknownString

  /**
   * The access level granted based on Age Assurance data we've processed.
   */
  access: 'unknown' | 'none' | 'safe' | 'full' | l.UnknownString

  /**
   * The ISO 3166-1 alpha-2 country code provided when beginning the Age Assurance flow.
   */
  countryCode: string

  /**
   * The ISO 3166-2 region code provided when beginning the Age Assurance flow.
   */
  regionCode?: string

  /**
   * The email used for Age Assurance.
   */
  email?: string

  /**
   * The IP address used when initiating the Age Assurance flow.
   */
  initIp?: string

  /**
   * The user agent used when initiating the Age Assurance flow.
   */
  initUa?: string

  /**
   * The IP address used when completing the Age Assurance flow.
   */
  completeIp?: string

  /**
   * The user agent used when completing the Age Assurance flow.
   */
  completeUa?: string
}

export type { Event }

/** Object used to store Age Assurance data in stash. */
const event = /*#__PURE__*/ l.typedObject<Event>(
  $nsid,
  'event',
  /*#__PURE__*/ l.object({
    createdAt: /*#__PURE__*/ l.string({ format: 'datetime' }),
    attemptId: /*#__PURE__*/ l.string(),
    status: /*#__PURE__*/ l.string<{
      knownValues: ['unknown', 'pending', 'assured', 'blocked']
    }>(),
    access: /*#__PURE__*/ l.string<{
      knownValues: ['unknown', 'none', 'safe', 'full']
    }>(),
    countryCode: /*#__PURE__*/ l.string(),
    regionCode: /*#__PURE__*/ l.optional(/*#__PURE__*/ l.string()),
    email: /*#__PURE__*/ l.optional(/*#__PURE__*/ l.string()),
    initIp: /*#__PURE__*/ l.optional(/*#__PURE__*/ l.string()),
    initUa: /*#__PURE__*/ l.optional(/*#__PURE__*/ l.string()),
    completeIp: /*#__PURE__*/ l.optional(/*#__PURE__*/ l.string()),
    completeUa: /*#__PURE__*/ l.optional(/*#__PURE__*/ l.string()),
  }),
)

export { event }
